import RetourQuery from '@/graphql/cms/RetourQuery.gql';
import BaseQuery from '@/graphql/cms/BaseQuery.gql';
import { stripLocale } from '@/utils/i18n';
import * as Sentry from '@sentry/vue';

const formatUri = (uri, locale) => {
  let result = stripLocale(uri, locale);

  if (!result.startsWith('/')) {
    result = '/' + result;
  }

  return result;
};

const transformBaseQuery = (base) => {
  return [
    { siteId: base.siteId, language: base.language },
    ...base.localized.map(({ siteId, language }) => ({ siteId, language })),
  ];
};

const fetchSiteIds = async () => {
  const { $craftRequest } = useNuxtApp();

  try {
    const { data: cachedData } = useNuxtData('siteIds');

    // Check if the data is already cached
    if (cachedData.value && cachedData.value.base) {
      return transformBaseQuery(cachedData.value.base);
    } else {
      // If not cached, fetch the data
      const { data } = await useAsyncData('siteIds', () => $craftRequest(BaseQuery));

      return transformBaseQuery(data.value.base);
    }
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

const processRetourRedirect = (retour) => {
  if (!retour.enabled) {
    return;
  }

  const { $localeRootPath } = useNuxtApp();

  const redirectHttpCode = retour.redirectHttpCode;
  const redirectDestUrl = retour.redirectDestUrl || '';

  console.info('execute retour redirect', {
    redirectHttpCode,
    redirectDestUrl,
  });

  switch (redirectHttpCode) {
    case 301:
    case 302:
      if (redirectDestUrl.startsWith('http')) {
        return navigateTo(redirectDestUrl, { redirectCode: redirectHttpCode });
      }

      return navigateTo($localeRootPath(redirectDestUrl), { redirectCode: redirectHttpCode });
    case 410:
      return showError({ statusCode: 410, message: 'Page is gone forever' });
    default:
      return;
  }
};

export default defineNuxtRouteMiddleware(async (to) => {
  const { $craftRequest, $getCurrentLocaleConfig } = useNuxtApp();

  // fetch altCode from the current loacale
  const { altCode: locale } = $getCurrentLocaleConfig();
  const uri = formatUri(to.path, locale);

  const siteIds = await fetchSiteIds();

  const currentSiteId = siteIds.find((site) => site.language === locale)?.siteId;

  if (uri && currentSiteId) {
    try {
      // request retour data
      const { retour } = await $craftRequest(RetourQuery, { uri, siteId: currentSiteId });

      // check if retour is not null
      if (retour) {
        return processRetourRedirect(retour);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }
});
